/**
 * Centralized user-facing copy for the Mail-on-Demand (MOD) feature.
 * No user-facing string should be hardcoded inside MOD components;
 * always reference this module and interpolate variables with formatMessage.
 */
export const formatMessage = (template, vars = {}) =>
  String(template).replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match,
  );

const strings = {
  common: {
    next: 'Next',
    back: 'Back',
    cancel: 'Cancel',
    retry: 'Try again',
    loading: 'Loading...',
    genericError: 'Something went wrong. Please try again.',
    notAvailable: 'Not available yet',
  },

  nav: {
    sidebarTitle: 'Mail on Demand',
    sidebarNewMailing: 'Send a mailing',
    sidebarOrders: 'My mail orders',
    sidebarCredits: 'Print credits',
  },

  wizard: {
    pageTitle: 'Send a mailing',
    stepAsset: 'Choose what to mail',
    stepAudience: 'Audience',
    stepReview: 'Review',
    stepPay: 'Pay',
    loadingOrder: 'Loading your order...',
    orderNotEditable: 'This order can no longer be edited.',
  },

  assets: {
    title: 'What would you like to mail?',
    subtitle: 'Pick one of your finalized books or template exports below.',
    loading: 'Loading your assets...',
    loadError: 'We could not load your assets. Please try again.',
    emptyTitle: 'Nothing ready to mail yet',
    emptyBody:
      'You need a finalized book or a completed template export before you can send a mailing. Finish one of your projects and come back here.',
    kindBook: 'Book',
    kindTbExport: 'Template export',
    templateExportName: 'Template export',
    templateExportOn: 'Template export · {date}',
    preview: 'Preview',
    previewBook: 'Preview book',
    previewPdf: 'Open PDF',
    beingPrepared: 'Being prepared for print',
  },

  coverLetter: {
    // Multi-asset entry (V4)
    addonTitle: 'Add a personal cover letter',
    addonNew: 'NEW',
    addonBody:
      'A one-page letter mailed with your book, personalized for every recipient — their name, your photo & brand. 2 designs to choose from.',
    addonCta: 'Personalize',
    summaryTitle: 'Your mailing',
    summaryBook: 'Book',
    summaryLetter: 'Cover letter',
    summaryLetterMeta: '{template} · personalized',
    summaryBundle: 'Bundle',
    summaryBundlePer: 'book + letter / recipient',
    summaryCredits: '{credits} credits',
    summaryRemove: 'Remove',
    summaryPreviewNote: "You'll preview the letter before paying.",
    addedChip: 'Cover letter added',
    includedChip: 'Cover letter · {template}',
    includedThumbAlt: 'Cover letter ({template}) included with this mailing',
    includedTitle: 'Cover letter included',
    includedBody: 'A personalized one-page letter is mailed with every book.',

    // Editor (V1)
    editorTitle: 'Personalize your cover letter',
    editorSubtitle:
      'Goes out with “{bookTitle}” — one letter per recipient, addressed to them.',
    prefilledChip: 'Prefilled from your profile',
    backToAssets: 'Back',
    templateLabel: 'Template',
    templateClassic: 'Classic',
    templateClassicHint: 'Bold real-estate bands',
    templateModern: 'Modern',
    templateModernHint: 'Clean sidebar & serif name',
    selected: 'Selected',
    messageLabel: 'Message',
    messageHint: 'Layout, header & signature stay fixed for print — you edit the wording.',
    insertHint: 'Insert:',
    insertLeadName: 'Lead first name',
    insertAgentName: 'Your name',
    brandingLabel: 'Your branding',
    brandingAutoFilled: 'auto-filled',
    brandingName: 'Name',
    brandingCompany: 'Company / brokerage',
    brandingPhone: 'Phone',
    brandingEmail: 'Email',
    brandingHeadshot: 'Headshot',
    brandingBrokerLogo: 'Broker logo',
    brandingOptional: 'optional',
    brandingBrokerLogoHint: 'Shown top-right. Skipped cleanly if not added.',
    brokerLogoCropTitle: 'Position your broker logo',
    brokerLogoCropHint: 'Drag to reposition and use the slider to zoom — this is exactly how it will appear on your letter.',
    brandingUpload: 'Upload',
    brandingUploading: 'Uploading…',
    brandingRemoveImage: 'Remove',
    brandingMissingTitle: 'Complete your branding',
    brandingMissingBody:
      'Add the missing details so every letter looks complete. We prefilled what we already had.',
    brandingLoadError: 'We could not load your branding. Please try again.',
    livePreview: 'Live preview',
    previewFor: 'Preview for: {name}',
    personalizedPerRecipient: 'Personalized per recipient',
    previewRecipientsNote: 'Each of your recipients gets their own copy, greeted by name.',
    sampleLeadName: 'Jane',
    saveAndContinue: 'Save & continue',
    remove: 'Remove letter',
    bundleChip: 'Book + letter bundle',
    perRecipient: '/ recipient',
    unavailable: 'Cover letters are not available right now.',
  },

  audience: {
    title: 'Who should receive it?',
    subtitle:
      'Choose the audience for this mailing from your Marketing Hub contacts. You will pick exactly who receives it in the next step.',
    nextStepHint:
      'Next, you will review the matched contacts and choose exactly who gets mail.',
    modeAll: 'All contacts',
    modeTags: 'Contacts with tags',
    tagsLabel: 'Tags',
    tagsPlaceholder: 'Enter tags separated by commas (e.g. buyers, past-clients)',
    tagsSelectorLabel: 'Pick the tags to mail',
    tagsSelectorHint: 'Select one or more tags. We will mail every contact that has any of them.',
    tagsLoading: 'Loading your tags...',
    tagsEmptyTitle: 'No tags yet',
    tagsEmptyBody:
      'None of your Marketing Hub contacts are tagged yet. Add tags in Marketing Hub, or type them below to target contacts by tag.',
    tagsLoadError:
      'We could not load your tags right now. Type them below instead, separated by commas.',
    tagsSearchPlaceholder: 'Search tags',
    tagsSelectedCount: '{count} selected',
    tagsNoMatch: 'No tags match your search.',
    previewLoading: 'Estimating your audience...',
    previewSummaryTitle: 'Your audience',
    previewTotal: '{totalCount} contacts match',
    previewMailable: 'About {estimatedMailable} of them look mailable',
    previewTotalLabel: 'Contacts matched',
    previewMailableLabel: 'Likely mailable',
    previewMailableShare: '{percent}% look mailable',
    previewEstimateLabel: 'Estimate',
    previewEstimateNote:
      'These numbers are an estimate. We will validate every address before you pay.',
    previewSampleTitle: 'Sample of matching contacts',
    previewSampleNoAddress: '(no mailing address yet)',
    noMarketingHubTitle: 'Marketing Hub account needed',
    noMarketingHubBody:
      'You need a Marketing Hub account with contacts before you can send a mailing. Open the Marketing Hub page to get set up.',
    noMarketingHubCta: 'Go to Marketing Hub',
    previewRetriableError: 'We could not reach your contacts right now.',
    suppressionGroupLabel: 'Contacts who asked not to be mailed',
    suppressionGroupHint:
      'These tags include people who opted out or can not receive mail. Only include them if you have a reason.',
    suppressionWarning:
      'You selected {tags} — these contacts asked not to be mailed.',
    suppressionAckLabel: 'I understand and want to include them anyway.',
    continue: 'Continue',
    submitting: 'Creating your order...',
  },

  resolution: {
    title: 'Preparing your mailing list',
    body:
      'We are checking every contact for a valid mailing address. This usually takes less than a minute.',
    failedTitle: 'We could not prepare your mailing list',
    failedBody: 'Something went wrong while validating your contacts.',
    retry: 'Retry',
  },

  review: {
    title: 'Review your mailing',
    mailableLabel: 'Recipients that will receive mail',
    // "Excluded" (not "Skipped") so the recipient label never collides with
    // the "Shipped" order status, and stays accurate for every reason —
    // duplicates DO have an address, so "No address" would mislabel them
    // (task 43). The precise reason shows in skipReason* below.
    skippedLabel: 'Contacts excluded',
    skipReasonsTitle: 'Why contacts were excluded',
    skipReasonMissingAddress: 'No mailing address',
    skipReasonIncompleteAddress: 'Incomplete mailing address',
    skipReasonDuplicate: 'Duplicate address',
    costLabel: 'Credits required',
    costPerRecipientOne: '1 credit per recipient',
    costPerRecipientMany: '{creditsPerRecipient} credits per recipient',
    minQuantityNotMet:
      'You need at least {minQuantity} selected recipients to place an order, but only {selectedCount} are selected. Select more contacts or fix excluded addresses.',
    continueToPay: 'Continue to payment',
    continueToPayWithCount: 'Continue to payment ({count})',
    selectedLabel: 'Will receive mail',
    selectedOfTotal: '{selected} of {total} selected',
    balanceLabel: 'Print credits balance',
    balanceAfter: 'after payment: {balance}',
    recipientsTitle: 'Recipients',
    colSelect: 'Select',
    colName: 'Name',
    colAddress: 'Address',
    colStatus: 'Status',
    colAction: 'Action',
    selectAll: 'Select all ({count})',
    selectNone: 'Select none',
    searchPlaceholder: 'Search name or city…',
    showingRange: 'Showing {count}',
    columnName: 'Name',
    columnAddress: 'Address',
    columnStatus: 'Status',
    statusMailable: 'Mailable',
    statusSkipped: 'Excluded',
    noAddress: 'No mailing address',
    addAddress: 'Add address',
    edit: 'Edit',
    corrected: 'Corrected',
    originalFromHub: 'Marketing Hub has: {address}',
    recipientsLoadError: 'We could not load your recipients. Please try again.',
    recipientsEmpty: 'No recipients match your search.',
    skippedGroup: 'Excluded ({count})',
    perPageLabel: 'Per page',
  },

  editAddress: {
    title: 'Edit mailing address — {name}',
    subtitle: 'This fixes the address for this mailing only.',
    fromHub: 'From Marketing Hub:',
    fromHubEmpty: '(no address on this contact)',
    address1: 'Street address',
    address2: 'Apt / Suite',
    city: 'City',
    state: 'State',
    zip: 'ZIP',
    country: 'Country',
    optional: 'Optional',
    nudge:
      'Also update this contact in Marketing Hub so future mailings use the right address.',
    cancel: 'Cancel',
    save: 'Save & make mailable',
    saving: 'Saving…',
    saveError: 'We could not save this address. Please check the fields and try again.',
    manualEntry: 'Enter address manually',
    useLookup: 'Search for an address',
    zipInvalid: 'Enter a valid ZIP / postal code.',
  },

  pay: {
    title: 'Confirm and pay',
    summaryAsset: 'Mailing',
    summaryRecipients: 'Recipients',
    summaryCost: 'Total credits',
    payButton: 'Pay {creditsRequired} credits',
    paying: 'Processing payment...',
    noChargeNote: 'You will not be charged until you confirm payment here.',
    insufficientTitle: 'Not enough print credits',
    insufficientBody:
      'This mailing needs {required} credits but you only have {balance}. Buy {shortfall} more credits to continue.',
    // Proactive shortfall nudge shown BEFORE the member clicks Pay, when the
    // balance can't cover the mailing (closes the dead-ended Pay → 402 loop).
    shortfallTitle: "You're {shortfall} credits short",
    shortfallBody:
      'This mailing needs {required} credits — you have {balance}. Buy more to continue.',
    payDisabledHint: 'Buy {shortfall} more credits to enable payment.',
    buyCredits: 'Buy credits',
    retryPay: 'Retry payment',
    payError: 'We could not process your payment. Please try again.',
    balanceAfterLabel: 'Balance after payment',
    processingResume: 'We are finishing your payment. This only takes a moment…',
    payingStuckTitle: 'Payment is taking longer than expected',
    payingStuckBody:
      'We have not been able to confirm your payment yet. You can try again, or check the order status.',
    payingRetry: 'Try again',
    payingViewOrder: 'Check order status',
    confirmationTitle: 'Your mailing is on its way!',
    confirmationBody: "We'll email you as your order moves through the print shop.",
    confirmationRecipientsLabel: 'Recipients',
    confirmationChargedLabel: 'Credits charged',
    confirmationRemainingLabel: 'Print credits remaining',
    confirmationAssetAlt: 'Cover of {title}',
    confirmationPreview: 'Preview',
    viewOrder: 'View order',
    viewCreditsHistory: 'Credits history',
    backToOrders: 'My mail orders',
  },

  orders: {
    pageTitle: 'My mail orders',
    newOrder: 'Send a mailing',
    loading: 'Loading your orders...',
    loadError: 'We could not load your orders. Please try again.',
    emptyTitle: 'No mail orders yet',
    emptyBody: 'When you send your first mailing it will show up here.',
    continueOrder: 'Continue your order',
    viewOrder: 'View',
    createdAt: 'Created {date}',
    recipientCountSingular: '{count} recipient',
    recipientsCount: '{count} recipients',
    filterAll: 'All',
    searchPlaceholder: 'Search by title',
    noMatch: 'No orders match your filter.',
  },

  detail: {
    pageTitle: 'Mail order',
    orderIdLabel: 'Order',
    copyOrderId: 'Copy order id',
    copied: 'Copied!',
    summaryTitle: 'Summary',
    trackCardCarrierLabel: 'Shipment reference · {carrier}',
    trackCardCarrierUnknownLabel: 'Shipment reference',
    trackCardButton: 'Track shipment',
    trackCardButtonAria: 'Track shipment on the {carrier} website (opens in a new tab)',
    trackCardUnknownNote:
      "We couldn't match this reference to a carrier. Copy it and look it up on your carrier's site.",
    shipmentRefNote:
      "This reference covers the whole mailing; individual recipients aren't tracked separately.",
    copyTrackingRef: 'Copy shipment reference',
    carrierUps: 'UPS',
    carrierFedex: 'FedEx',
    carrierUsps: 'USPS',
    cancelOrder: 'Cancel order',
    cancelConfirmTitle: 'Cancel this order?',
    cancelConfirmBody: 'This mailing will not be sent.',
    cancelConfirmRefundNote: 'Your {creditsRequired} credits will be refunded.',
    cancelConfirmYes: 'Yes, cancel order',
    cancelConfirmNo: 'Keep order',
    cancelledBanner: 'This order was cancelled.',
    failedBanner: 'We could not prepare this mailing. Continue the order to retry.',
    loadError: 'We could not load this order. Please try again.',
    stepSubmitted: 'Submitted',
    stepPaid: 'Paid',
    stepAccepted: 'Accepted',
    stepInProduction: 'In production',
    stepShipped: 'Shipped',
  },

  statuses: {
    draft: 'Draft',
    resolving: 'Preparing audience',
    resolution_failed: 'Needs attention',
    review: 'Ready for review',
    paying: 'Processing payment',
    paid: 'Paid',
    accepted: 'Accepted',
    in_production: 'In production',
    shipped: 'Shipped',
    cancelled: 'Cancelled',
  },

  credits: {
    pageTitle: 'Print credits',
    balanceLabel: 'Print credits balance',
    balanceUnit: 'credits',
    buyMore: 'Buy credits',
    historyTitle: 'Credit history',
    historyLoading: 'Loading your credit history...',
    historyLoadError: 'We could not load your credit history. Please try again.',
    historyEmpty: 'No credit activity yet.',
    columnDate: 'Date',
    columnReason: 'Activity',
    columnDelta: 'Credits',
    columnBalanceAfter: 'Balance',
    previousPage: 'Previous',
    nextPage: 'Next',
    pageIndicator: 'Page {currentPage} of {lastPage}',
  },
};

export default strings;
